import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Check, ChevronDown, CircleSlash, Pencil, Printer } from 'lucide-react';

import { downloadBomExcel } from '#/api/momMgmt/settings/bom/downloadExcel';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import {
  pickAvailableWorkflowExposedIds,
  resolveLgDesktopLayout,
  resolveMdDesktopLayout,
  resolveMobileLayout,
} from '#/modules/imsMgmt/receipts/shared/documentCommandBarLayout';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import { getBomApiErrorMessage } from '#/modules/momMgmt/settings/bom/helper/apiError';
import {
  canActivateBom,
  canDeactivateBom,
} from '#/modules/momMgmt/settings/bom/helper/bomStatus';
import useBomStatusMutation, {
  BOM_ACTIVATE_CONFIRM,
  BOM_DEACTIVATE_CONFIRM,
} from '#/modules/momMgmt/settings/bom/hooks/useBomStatusMutation';
import { getFileTransferErrorMessage } from '#/utils/blobResponseError';
import { triggerBlobDownload } from '#/utils/downloadBlob';
import { toast } from '#/utils/toast';

const WORKFLOW_EXPOSED_ORDER = ['deactivate', 'activate'];

const ACTION_GROUPS = [
  { key: 'content', label: '單據內容', ids: ['edit', 'print'] },
  { key: 'status', label: '狀態', ids: ['activate', 'deactivate'] },
];

const ACTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  to: PropTypes.string,
  onSelect: PropTypes.func,
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  available: PropTypes.bool,
  showExposedIcon: PropTypes.bool,
  exposedVariant: PropTypes.string,
  exposedClassName: PropTypes.string,
});

function getExposedActionIds(actionMap) {
  return pickAvailableWorkflowExposedIds(actionMap, WORKFLOW_EXPOSED_ORDER);
}

function runAfterMenuClose(callback) {
  window.setTimeout(() => {
    callback?.();
  }, 0);
}

function BomExposedActionButton({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);

  if (action.to && !disabled) {
    return (
      <Button
        asChild
        variant={action.exposedVariant || 'default'}
        className={cn(action.exposedClassName)}
      >
        <Link to={action.to} aria-label={action.label}>
          {action.showExposedIcon && Icon ? (
            <Icon className="size-3.5" aria-hidden />
          ) : null}
          {action.label}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={action.destructive ? 'destructive' : (action.exposedVariant || 'default')}
      className={cn(
        action.destructive && DESTRUCTIVE_SOLID_BUTTON_CLASS,
        action.exposedClassName,
      )}
      disabled={disabled}
      onClick={() => action.onSelect?.()}
    >
      {action.showExposedIcon && Icon ? (
        <Icon className="size-3.5" aria-hidden />
      ) : null}
      {action.label}
    </Button>
  );
}

BomExposedActionButton.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function BomMenuItem({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = Icon ? <Icon className="size-4" aria-hidden /> : null;

  if (action.to && !disabled) {
    return (
      <DropdownMenuItem
        asChild
        variant={action.destructive ? 'destructive' : 'default'}
      >
        <Link to={action.to}>
          {iconNode}
          {action.label}
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      variant={action.destructive ? 'destructive' : 'default'}
      disabled={disabled}
      onSelect={() => {
        if (disabled) {
          return;
        }
        runAfterMenuClose(action.onSelect);
      }}
    >
      {iconNode}
      {action.label}
    </DropdownMenuItem>
  );
}

BomMenuItem.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function BomActionsMenu({
  triggerLabel,
  triggerAriaLabel,
  groups,
  isPending,
}) {
  if (!groups.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          aria-label={triggerAriaLabel || triggerLabel}
        >
          {triggerLabel}
          <ChevronDown className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn('w-auto min-w-44')}>
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((action) => (
              <BomMenuItem
                key={action.id}
                action={action}
                isPending={isPending}
              />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

BomActionsMenu.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  triggerAriaLabel: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(ACTION_SHAPE).isRequired,
  })).isRequired,
  isPending: PropTypes.bool,
};

const BomCommandBar = ({ detail, onRefetch }) => {
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canUpdate = useCan(PERM.BOM_UPDATE);
  const canActivatePerm = useCan(PERM.BOM_ACTIVATE);

  const id = detail?.id;
  const status = detail?.status;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;

  const statusMutation = useBomStatusMutation({
    id,
    getUpdatedAt: () => updatedAt,
    onAfterSuccess: async () => {
      await onRefetch?.();
    },
    onVersionConflict: () => {
      onRefetch?.();
    },
  });

  const isPending = statusMutation.isPending;

  async function handlePrintExcel() {
    if (!id || isDownloadingExcel) {
      return;
    }

    setIsDownloadingExcel(true);
    try {
      const { blob, filename } = await downloadBomExcel(id);
      triggerBlobDownload(blob, filename);
    } catch (error) {
      toast.error(
        getFileTransferErrorMessage(error)
        || getBomApiErrorMessage(error, '下載 Excel 失敗'),
      );
    } finally {
      setIsDownloadingExcel(false);
    }
  }

  async function handleActivate() {
    const ok = await confirm(BOM_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(BOM_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  const actionMap = {
    print: {
      id: 'print',
      label: isDownloadingExcel ? '下載中…' : '列印',
      icon: Printer,
      available: Boolean(id),
      disabled: !id || isDownloadingExcel,
      onSelect: handlePrintExcel,
    },
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: id ? ROUTES.bomEdit(id) : undefined,
      available: Boolean(id) && canUpdate,
      showExposedIcon: true,
      exposedVariant: 'default',
      exposedClassName: 'document-detail-edit-action',
    },
    activate: {
      id: 'activate',
      label: '啟用',
      icon: Check,
      available: canActivatePerm && canActivateBom(status),
      onSelect: handleActivate,
    },
    deactivate: {
      id: 'deactivate',
      label: '停用',
      icon: CircleSlash,
      available: canActivatePerm && canDeactivateBom(status),
      destructive: true,
      onSelect: handleDeactivate,
    },
  };

  const mobileLayout = resolveMobileLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
  });
  const mdLayout = resolveMdDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });
  const lgLayout = resolveLgDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });

  function renderLayoutRow(
    layout,
    { triggerLabel = '更多', triggerAriaLabel = '更多操作' } = {},
  ) {
    return (
      <>
        {layout.flatActions.map((action) => (
          <BomExposedActionButton
            key={action.id}
            action={action}
            isPending={isPending}
          />
        ))}
        {layout.showMoreMenu ? (
          <BomActionsMenu
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            groups={layout.menuGroups}
            isPending={isPending}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {ConfirmDialogHost}
      <div className={cn('flex items-center justify-end gap-2')}>
        <div className={cn('flex md:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mobileLayout, {
            triggerLabel: '操作',
            triggerAriaLabel: '單據操作',
          })}
        </div>
        <div className={cn('hidden md:flex lg:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mdLayout)}
        </div>
        <div className={cn('hidden lg:flex', 'items-center gap-2')}>
          {renderLayoutRow(lgLayout)}
        </div>
      </div>
    </>
  );
};

BomCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default BomCommandBar;
